import { memo, useRef } from 'react';
import { Maximize2, Minimize2, MonitorUp } from 'lucide-react';
import { EmptyState, IconButton } from '@cyphex/ui';
import { useFullscreen } from '../hooks/useFullscreen';
import { useVideoAspect } from '../hooks/useVideoAspect';
import type { MediaClient } from '../media/contract';
import type { TileView } from '../media/tilePlan';
import { VideoTile } from './VideoTile';

export interface PresentationLayoutProps {
  tiles: readonly TileView[];
  shareTile: TileView | null;
  client: MediaClient;
  pinnedId: string | null;
  onPin: (participantId: string | null) => void;
}

/**
 * The single biggest bandwidth lever in the product (study §4.2): the share
 * goes large and every camera drops to a 180p thumbnail.
 *
 * The share is never CROPPED — see `VideoTile`'s `fit`. Cropping a shared
 * screen is not a cosmetic choice: it silently eats whatever lives at the
 * edges, which is the dock, the tab bar, the line numbers and, often, the one
 * cell somebody is actually pointing at.
 *
 * But it is no longer letterboxed into the whole stage either. The share box
 * now takes the shape of the SCREEN BEING SHARED and is centred, so on a wide
 * monitor the leftover width is flat stage instead of a pair of grey slabs
 * bolted to the sides of the picture. Those slabs were `bg-stage-tile`, which
 * is LIGHTER than `bg-stage` (12% vs 6% lightness in dark mode) — so the wasted
 * space did not read as empty background, it read as part of the video, and a
 * 16:9 desktop on a 21:9 window looked like it had been framed in grey.
 *
 * The video itself does not get any smaller: in the common case it is limited
 * by the stage's HEIGHT, so narrowing the box to its true aspect takes away
 * only the bars.
 */
export const PresentationLayout = memo(function PresentationLayout({
  tiles, shareTile, client, pinnedId, onPin,
}: PresentationLayoutProps) {
  const fullscreen = useFullscreen();
  /* The share box is sized to the shared screen's own shape; see the note above
     about why grey bars are worse than empty stage. */
  const shareRef = useRef<HTMLDivElement>(null);
  const shareAspect = useVideoAspect(shareRef);

  return (
    <div className="flex h-full min-h-0 w-full flex-col gap-2 lg:flex-row">
      {/* `min-w-0` + `flex-1` so the region can still shrink, but the share
          inside it is centred rather than stretched — see the note above. */}
      <div
        ref={fullscreen.ref}
        /* `flex-1` RESTORED.
           Dropping it so the strip could absorb the leftover width collapsed
           the strip instead: it still carried `shrink-0`, so with `flex-1`'s
           0% basis it never grew, and the participant tiles disappeared from
           the stage altogether. A fixed but wider strip is the honest trade —
           the tiles always render, and the share is centred in what is left.
           The share itself cannot grow into that space regardless: it is
           bounded by the stage's HEIGHT, not its width. */
        className="relative flex min-h-0 min-w-0 flex-1 items-center justify-center overflow-hidden"
      >
        {shareTile ? (
          <>
            <div
              ref={shareRef}
              /* THE SHARE MUST OWN ITS EDGE.
                 The letterbox bars used to be the boundary — dark slabs around
                 the picture told you where the shared screen stopped. Now that
                 the box hugs the screen, nothing does, and `--stage` is PURE
                 WHITE in light mode (0 0% 100%). A shared desktop is mostly
                 white too, so without a border the share bled into the page and
                 you could not tell where somebody's screen ended and our UI
                 began. A ring costs one line and works on either theme. */
              /* `group/share` is NAMED deliberately: the VideoTile inside
                 owns a plain `group`, and an unnamed one here would make
                 `group-hover:` ambiguous between the two. */
              className="group/share relative h-full max-w-full overflow-hidden rounded-md bg-stage-tile ring-1 ring-border shadow-tile"
              /* The ONE inline style in this file, and it cannot be a class:
                 the ratio is whatever the presenter's screen happens to be, and
                 Tailwind's arbitrary values have to be statically analysable.
                 Until the first frame reports its size we fall back to filling
                 the region, which is exactly the old behaviour. */
              style={shareAspect === null ? undefined : { aspectRatio: shareAspect }}
            >
              <VideoTile
                tile={shareTile}
                client={client}
                source="screen"
                fit="contain"
                className="h-full w-full"
              />
              {/* Inside the share box, so it sits on the picture rather than
                  floating in the empty stage beside it. */}
              {fullscreen.supported ? (
                <IconButton
                  icon={fullscreen.isFullscreen ? Minimize2 : Maximize2}
                  label={
                    fullscreen.isFullscreen
                      ? 'Exit full screen'
                      : 'View shared screen full screen'
                  }
                  variant="stage"
                  size="md"
                  onClick={fullscreen.toggle}
                  /* Same rule as the nameplate: a control parked
                     permanently on top of somebody's shared window covers the
                     very content they are presenting — here, the top-right
                     corner of their screen. It returns on hover, and on
                     keyboard focus so it stays reachable without a pointer. */
                  className="absolute right-2 top-2 z-10 opacity-0 transition-opacity duration-fast focus-visible:opacity-100 group-hover/share:opacity-100"
                />
              ) : null}
            </div>
          </>
        ) : (
          <EmptyState
            icon={MonitorUp}
            title="Nobody is sharing"
            description="Start a share to present slides, an app window or a browser tab."
            size="md"
            className="h-full"
          />
        )}
      </div>

      {/* Rendered only when there is somebody in it: an empty strip still
          reserved 176px of the stage, and every one of those pixels comes
          straight off the width of the shared screen. */}
      {tiles.length === 0 ? null : (
        /* THE STRIP TAKES THE LEFTOVER, instead of it sitting empty beside
           the share. It was pinned at 176px, so on a wide window a 16:9 share
           left a slab of bare stage between the two — the thing that kept
           getting reported as "blank space". Growing the strip spends that
           width on something worth looking at: the faces get bigger.

           288px rather than the original 176px: wider faces spend some of the
           space the share cannot use. It is FIXED, not `flex-1` — see the
           note on the share region for what happened when it was not. */
        <div className="flex shrink-0 gap-2 overflow-x-auto overflow-y-hidden lg:w-72 lg:flex-col lg:overflow-y-auto lg:overflow-x-hidden">
          {tiles.map((tile) => (
            <VideoTile
              key={tile.participant.id}
              tile={tile}
              client={client}
              pinned={pinnedId === tile.participant.id}
              onPin={onPin}
              className="aspect-video h-20 w-auto shrink-0 lg:h-auto lg:w-full"
            />
          ))}
        </div>
      )}
    </div>
  );
});
